import { useIntl } from "react-intl";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Typography, Container, Paper, Divider, Icon } from "@mui/material";
import moment from 'moment';

//Components
import { NotificationManager } from 'Components/Notifications';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Filters from '../.components/filters'
import { NavLink } from "react-router-dom";

//Actions
import { powerLostRequest, clearRedux } from 'Redux/actions';

//Helpers
import cx from 'classnames';
import { getDateFormatted, validateEpochDate, copyToClipboard } from 'Core/data/Helpers';


const ROWS_PER_PAGE = 20;
const RANGE_MODE_ROWS = 50;
const FORCE_CURRENT_WEEK_RANGE = false; // true = always use current week dates on mount, ignoring saved preferences

const PowerLost = (props) => {
    const { timezone } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();

    const { Items, loading, hasMore, errorCode, error } = useSelector(state => state.powerLostRedux);
    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);

    const [columns, setColumns] = useState([
        {
            key: "camerasn",
            title: messages['vidfleet_camera_sm'],
            show: true,
            locked: true,
            resize: true,
            export: true,
            width: 150,
            exportCell: "cameraSN",
            code: "vidfleet_camera_sm",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item?.cameraSN}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.cameraSN}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "reason",
            title: messages['vidfleet_reason'],
            show: true,
            resize: true,
            export: true,
            exportCell: "reason",
            code: "vidfleet_reason",
            width: 250,
            render: (item) => {
                return (
                    <span>{item?.reason}</span>
                )
            }
        },
        {
            key: "poweredBy",
            title: messages['vidfleet_poweredBy'],
            show: true,
            resize: true,
            export: true,
            exportCell: "poweredBy",
            code: "vidfleet_poweredBy",
            width: 150,
            render: (item) => {
                return (
                    <span>{item?.poweredBy}</span>
                )
            }
        },
        {
            key: "createtime",
            title: messages['vidfleet_created_time'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "createTime",
            exportFormat: "datetime",
            code: "vidfleet_created_time",
            width: 250,
            render: (item) => {
                const createTimeValid = validateEpochDate(item?.createTime)
                return <span>{createTimeValid && item?.createTime && item?.createTime !== -1 && getDateFormatted(item?.createTime, "date", "datetime", null, timezone)}</span>
            }
        },
        {
            key: "powerofftime",
            title: messages['vidfleet_power_off_time'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "removedTime",
            exportFormat: "datetime",
            code: "vidfleet_power_off_time",
            width: 250,
            render: (item) => {
                const connectedTimeValid = validateEpochDate(item?.removedTime)
                return <span>{connectedTimeValid && item?.removedTime && item?.removedTime !== -1 && getDateFormatted(item?.removedTime, "date", "datetime", null, timezone)}</span>
            }
        },
        {
            key: "recovertime",
            title: messages['vidfleet_recover_time'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "connectedTime",
            exportFormat: "datetime",
            code: "vidfleet_recover_time",
            width: 250,
            render: (item) => {
                const connectedTimeValid = validateEpochDate(item?.connectedTime)
                return <span>{connectedTimeValid && item?.connectedTime && item?.connectedTime !== -1 && getDateFormatted(item?.connectedTime, "date", "datetime", null, timezone)}</span>
            }
        },
        {
            key: "lastGpsData",
            title: messages['vidfleet_lastGpsData'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "vidfleet_lastGpsData",
            width: 300,
            render: (item) => {
                let currentCoordinates = null
                if (item?.lastGpsData?.coordinate.length) {
                    currentCoordinates = `Lat: ${item?.lastGpsData?.coordinate[0]} Lng: ${item?.lastGpsData?.coordinate[1]}`
                }
                return (
                    <div style={{ display: 'flex' }}>
                        {
                            currentCoordinates ? (
                                <>
                                    <span>{currentCoordinates}</span>
                                    <Icon style={{ cursor: "pointer", marginLeft: 5, color: 'rgba(0, 0, 0, 0.54)', fontSize: 18 }} className="toolbar-list-icon" onClick={() => { copyToClipboard(currentCoordinates, messages['common_copied']) }}>
                                        content_copy
                                    </Icon>

                                </>
                            ) : <></>
                        }
                    </div>
                )
            },
            exportValue: (item) => {
                let currentCoordinates = null
                if (item?.lastGpsData?.coordinate.length) {
                    currentCoordinates = `Lat: ${item?.lastGpsData?.coordinate[0]} Lng: ${item?.lastGpsData?.coordinate[1]}`
                }
                return currentCoordinates || ''
            }
        },
    ]);

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(FORCE_CURRENT_WEEK_RANGE ? tempStartDate : (rawDataFilters?.startDate || tempStartDate));
    const [endDate, setEndDate] = useState(FORCE_CURRENT_WEEK_RANGE ? new Date() : (rawDataFilters?.endDate || new Date()));
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [last, setLast] = useState(rawDataFilters?.last || { id: '20', name: '20' });
    const [type, setType] = useState(rawDataFilters?.type || 'last');
    const [esn, setEsn] = useState(props?.single ? props?.device?.esn || "" : "");

    const getData = (refresh, paramType) => {
        const currentType = paramType ?? type;
        if (refresh) dispatch(clearRedux("POWER_LOST", { hasMore: false, Items: [] }));

        let currentStarDate = new Date(startDate);
        let currentEndDate = new Date(endDate);

        let payload = {
            startDate: currentStarDate.setHours(0),
            endDate: currentEndDate.setHours(23, 59, 59),
            from: refresh ? 0 : Items.length || 0,
            limit: currentType === "last" ? last.id : ROWS_PER_PAGE,
            hasMore: refresh ? false : currentType === "last" ? false : hasMore,
            filterByDate: currentType === "range",
            esn: esn
        }
        if (payload.startDate && payload.endDate) {
            payload.startDate = new Date(payload.startDate)
            payload.endDate = new Date(payload.endDate)
        }

        dispatch(powerLostRequest(payload));
        tableRef?.current?.conditionsHandler(payload)
    }

    const handleLast = (type) => {
        dispatch(clearRedux("POWER_LOST", { hasMore: false }))
        setType(type);
        if (type === 'range') {
            tableRef?.current?.setRows(RANGE_MODE_ROWS);
        }
        getData(true, type);
    }

    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences) => {
        let currentStartDate = new Date(startDate);
        let currentEndDate = new Date(endDate);
        let currentType = type;

        // Sync local state from preferences when loaded from saved preferences
        if (fromPreferences && newConditions) {
            if (newConditions.filterByDate && newConditions.startDate) {
                currentType = 'range';
                setType('range');
                if (!FORCE_CURRENT_WEEK_RANGE) {
                    currentStartDate = new Date(newConditions.startDate);
                    currentEndDate = new Date(newConditions.endDate);
                    setStartDate(currentStartDate);
                    setEndDate(currentEndDate);
                    setBeforeRangeDate({
                        start: currentStartDate,
                        end: currentEndDate
                    });
                }
            } else if (newConditions.limit) {
                currentType = 'last';
                setType('last');
                setLast({ id: String(newConditions.limit), name: String(newConditions.limit) });
            }
        }

        let payload = {
            startDate: new Date(currentStartDate).setHours(0),
            endDate: new Date(currentEndDate).setHours(23, 59, 59),
            from: 0,
            limit: currentType === "last" ? newConditions?.limit || last?.id : ROWS_PER_PAGE,
            moreRecords: false,
            hasMore: false,
            type: currentType,
            filterByDate: currentType === "range",
            esn: esn,
        };

        if (payload.startDate && payload.endDate) {
            payload.startDate = new Date(payload.startDate);
            payload.endDate = new Date(payload.endDate);
        }

        dispatch(powerLostRequest(payload));
    };

    useEffect(() => {

        return () => {
            dispatch(clearRedux("POWER_LOST"));
        };

    }, [])

    useEffect(() => {
        if (error) {
            if (esn) {
                NotificationManager.error(
                    errorCode === 403 ? messages['vidfleet_the_esn_could_not_be_consulted'] : messages['vidfleet_the_esn_does_not_exist'],
                    messages['vidfleet_power_lost'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            else {
                NotificationManager.error(
                    messages['vidfleet_error_power_lost'],
                    messages['vidfleet_power_lost'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            dispatch(clearRedux("POWER_LOST", { error: null, errorCode: null }));
        }
    }, [error]);

    return (
        <Container maxWidth={false} className={cx("vidfleet-power-lost-tab", !props?.single ? "container-view" : "container-view-tab")}>
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: !props?.single ? "14px 18px" : 0, borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    {
                        !props?.single &&
                        <Grid item xs={6}>
                            <Typography variant="h5" gutterBottom component="div">
                                {messages['vidfleet_power_lost']}
                            </Typography>
                        </Grid>
                    }
                    {
                        !props?.single &&
                        <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    }
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        name="pms-powerlost-v1"
                        showTotalCount={false}
                        exportConfig={{
                            fileName: "powerlost",
                            disablePDF: true,
                            useDataGrid: true,
                        }}
                        externalDataConfig={{
                            data: Items,
                            loading,
                            nextToken: type === "last" ? false : hasMore,
                            onRefresh: () => getData(true),
                            totalCount: Items?.length || 0,
                            totalCountLoading: loading,
                        }}
                        columns={columns}
                        uiConfig={{
                            topHeight: props?.single ? 286 : 200,
                            hideSplit: true,
                            hideFilters: true,
                        }}
                        searchConfig={{
                            disabled: true,
                        }}
                        selectionConfig={{
                            disabled: true,
                        }}
                        infiniteScroll={type !== 'last'}
                        onChangePage={() => getData()}
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        extraFilters={() => {
                            return (
                                <Filters
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    setBeforeRangeDate={setBeforeRangeDate}
                                    beforeRangeDate={beforeRangeDate}
                                    last={last}
                                    setLast={setLast}
                                    onChangeFilters={() => getData(true)}
                                    type={type}
                                    setType={handleLast}
                                    esn={esn}
                                    setEsn={setEsn}
                                    disabled={loading}
                                    single={props?.single}
                                    module={props?.module}
                                    setModule={props?.setModule}
                                />
                            )
                        }}
                        flexEnd
                        onChangeConditions={handleChangeConditions}
                    />
                </Grid>
            </Paper>
        </Container>
    )
}

export default PowerLost;
